// ═══════════════════════════════════════════════════════════════════════
// API CLIENT — Alrifai backend
// Generated to match Alrifai.postman_collection.json (35 endpoints) exactly.
//
// • Envelope:  { key: 'success' | 'error', msg: string, data?: any }
// • GET responses return the `data` field (object | array).
// • POST endpoints (contact, subscribe, comments, likes) use multipart
//   form-data — matching the Postman "formdata" request bodies.
// • Base URL is configurable. The default mirrors the collection's pagination
//   URLs (…/api/v1/). Override with NEXT_PUBLIC_API_URL in .env.local, e.g.
//       NEXT_PUBLIC_API_URL=https://api.alrifai.bio/api/v1/
// ═══════════════════════════════════════════════════════════════════════

const BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'http://127.0.0.1:8000/api/v1/').replace(/\/$/, '') + '/'

type Envelope<T> = { key: string; msg: string; data?: T }

async function get<T>(endpoint: string): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${endpoint}`, {
      headers: { Accept: 'application/json' },
      next: { revalidate: 60 },
    })
    if (!res.ok) return null
    const j = (await res.json()) as Envelope<T>
    return j.key === 'success' ? (j.data as T) : null
  } catch {
    return null
  }
}

// All POST endpoints in the collection send multipart/form-data.
async function postForm<T>(
  endpoint: string,
  fields: Record<string, string | undefined>,
): Promise<{ ok: boolean; msg: string; data: T | null }> {
  try {
    const fd = new FormData()
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, v)
    })
    const res = await fetch(`${BASE}${endpoint}`, {
      method: 'POST',
      headers: { Accept: 'application/json' }, // do NOT set Content-Type; the browser adds the boundary
      body: fd,
    })
    const j = (await res.json()) as Envelope<T>
    return { ok: j.key === 'success', msg: j.msg, data: (j.data ?? null) as T | null }
  } catch {
    return { ok: false, msg: 'network_error', data: null }
  }
}

// ─── Types (exactly as returned by the API) ──────────────────────────────

export type Slider = { id: number; image: string; title: string; description: string }

export type Story = { id: number; image: string; description: string }

export type ProjectCard = {
  id: number
  image: string
  title: string
  slug?: string
  created_by: string
}

export type OurMission = { id: number; image: string; title: string; description: string }

export type CoreValue = { id: number; image: string; title: string; description: string; quotes: string }

export type BlogPost = {
  id: number
  image: string
  slug?: string
  title: string
  description: string
  created_at: string
  likes_count: number
  comments_count: number
  status?: number
  category?: Category
}

export type Category = { id: number; name: string; slug: string; image?: string; icon?: string }

export type BlogStatus = { name: string; type: string }

export type Tag = { id: number; tag: string; slug: string }

export type Comment = { id: number; name: string; email: string; website?: string; comment: string; created_at: string }

export type Inspiration = {
  id: number
  image: string
  slug?: string
  title: string
  description: string
  created_at: string
  likes_count: number
  comments_count: number
  status?: number
  category?: Category
  tags?: Tag[]
  liked?: boolean
  images?: string[]
}

export type Business = { id: number; icon: string; title: string; description: string }

export type VisionItem = { id: number; icon: string; title: string }

export type MissionCard = { id: number; title: string; contents: { id: number; icon: string; content: string }[] }

export type AboutSlider = { id: number; icon: string; title: string; description: string }

export type Team = {
  id: number
  image: string
  full_name: string
  job_title: string
  description: string
  personal_statement?: string
  address?: string
  phone?: string
  email?: string
  website?: string
  facebook?: string
  google_plus?: string
  vimeo?: string
  pinterest?: string
}

export type Social = { id: number; icon: string; link: string }

export type Insight = { id: number; title: string; video_url?: string; published_at?: string; [k: string]: any }

export type Project = {
  id: number
  image: string
  title: string
  slug: string
  created_by: string
  description: string
  client_name?: string
  pdf?: string
  doc?: string
  online_preview?: string
  project_category_id?: number
  project_category_name?: string
  starting_date?: string
  starting_investment?: string
  roi?: string
  link?: string
  services_title?: string
  images?: string[]
  project_managements?: any[]
  project_services?: any[]
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
}

export type Setting = { key: string; value: string }

export type Pagination = {
  total_items: number
  count_items: number
  per_page: number
  total_pages: number
  current_page: number
  next_page_url: string
  perv_page_url: string
}

// ─── 01 · Home  (GET home) ───────────────────────────────────────────────

export const getHomeData = () =>
  get<{
    sliders: Slider[]
    stories: Story[]
    coming_soon_projects: ProjectCard[]
    our_missions: OurMission[]
    core_values: CoreValue[]
    blogs: BlogPost[]
  }>('home')

// ─── 02-05 · About ───────────────────────────────────────────────────────

export const getBiography = () =>
  get<{ about_sliders: AboutSlider[]; teams: Team[]; blogs: BlogPost[] }>('about/biography')

export const getVision = () =>
  get<{ core_values: VisionItem[]; latest_blogs: BlogPost[] }>('about/vision')

export const getMission = () =>
  get<{ core_values: MissionCard[]; latest_blogs: BlogPost[] }>('about/mission')

export const getCoreValues = () =>
  get<{ our_core_values: CoreValue[]; latest_blogs: BlogPost[] }>('about/our-core-values')

// ─── 06 · Teams (GET teams/{id}) ─────────────────────────────────────────

export const getTeamDetails = (id: number) => get<Team>(`teams/${id}`)

// ─── 07-08 · Businesses ──────────────────────────────────────────────────

export const getBusinessesPage = () =>
  get<{
    businesses: Business[]
    project_categories: { id: number; name: string }[]
    current_projects: ProjectCard[]
    coming_soon_projects: ProjectCard[]
    latest_blogs: BlogPost[]
  }>('businesses-page')

export const getBusinessDetails = (id: number) =>
  get<{ business: Business; businesses: Business[] }>(`businesses/${id}`)

// ─── 09 · Project details (GET projects/{slug}) ──────────────────────────

export const getProjectDetails = (slug: string) => get<Project>(`projects/${slug}`)

// ─── 10-19 · Blogs ───────────────────────────────────────────────────────

export const getBlogsPage = () =>
  get<{ latest_blogs: BlogPost[]; categories: Category[]; blog_statuses: BlogStatus[] }>('blogs-page')

export const getBlogs = (p?: { created_at?: string; category_id?: number; status?: string; page?: number; paginate?: number }) => {
  const q = new URLSearchParams()
  if (p?.created_at) q.set('created_at', p.created_at)
  if (p?.category_id) q.set('category_id', String(p.category_id))
  if (p?.status) q.set('status', p.status)
  q.set('page', String(p?.page || 1))
  q.set('paginate', String(p?.paginate || 10))
  return get<{ blogs: BlogPost[]; pagination: Pagination }>(`blogs?${q}`)
}

export const getBlogPost = (slug: string) =>
  get<{ blog: BlogPost; categories: Category[]; popular_news: BlogPost[]; popular_tags: Tag[] }>(`blogs/${slug}`)

export const getBlogCategories = () => get<Category[]>('blog-categories')

export const getCategoryBlogs = (id: number, page = 1, paginate = 20) =>
  get<{ category: Category; blogs: BlogPost[]; pagination: Pagination }>(`blog-categories/${id}?page=${page}&paginate=${paginate}`)

export const getLatestBlogs = (popularTagSlug?: string) =>
  get<BlogPost[]>(`latest-blogs${popularTagSlug ? `?popular_tag_slug=${encodeURIComponent(popularTagSlug)}` : ''}`)

export const getPopularTagBlogs = (slug: string) =>
  get<{ blogs: BlogPost[]; pagination: Pagination }>(`popular-tag-blogs/${slug}`)

export const getTagBlogs = (tag: string, page = 1, paginate = 10) =>
  get<{ blogs: BlogPost[]; pagination: Pagination }>(`tag-blogs?tag=${encodeURIComponent(tag)}&page=${page}&paginate=${paginate}`)

export const getBlogComments = (id: number, page = 1, paginate = 20) =>
  get<{ comments: Comment[]; pagination: Pagination }>(`blogs/${id}/comments?page=${page}&paginate=${paginate}`)

export const addBlogComment = (id: number, body: { name: string; email: string; website?: string; comment: string }) =>
  postForm<{ comment: Comment }>(`blogs/${id}/comments`, body)

export const toggleBlogLike = (id: number) =>
  postForm<{ likes_count: number; liked: boolean }>(`blogs/${id}/like`, {})

// ─── 20-28 · Inspirations ────────────────────────────────────────────────

export const getInspirations = (p?: { category_id?: number; page?: number; paginate?: number }) => {
  const q = new URLSearchParams()
  if (p?.category_id) q.set('category_id', String(p.category_id))
  q.set('page', String(p?.page || 1))
  q.set('paginate', String(p?.paginate || 10))
  return get<{ inspirations: Inspiration[]; pagination: Pagination }>(`inspirations?${q}`)
}

export const getInspirationPost = (slug: string) =>
  get<{ inspiration: Inspiration; categories: Category[]; popular_news: BlogPost[]; popular_tags: Tag[] }>(`inspirations/${slug}`)

export const getInspirationCategories = () => get<Category[]>('inspiration-categories')

export const getCategoryInspirations = (id: number, page = 1, paginate = 20) =>
  get<{ category: Category; inspirations: Inspiration[]; pagination: Pagination }>(`inspiration-categories/${id}?page=${page}&paginate=${paginate}`)

export const getLatestInspirations = (popularTagSlug?: string) =>
  get<Inspiration[]>(`latest-inspirations${popularTagSlug ? `?popular_tag_slug=${encodeURIComponent(popularTagSlug)}` : ''}`)

export const getTagInspirations = (tag: string, page = 1, paginate = 10) =>
  get<{ inspirations: Inspiration[]; pagination: Pagination }>(`tag-inspirations?tag=${encodeURIComponent(tag)}&page=${page}&paginate=${paginate}`)

export const getInspirationComments = (id: number, page = 1, paginate = 20) =>
  get<{ comments: Comment[]; pagination: Pagination }>(`inspirations/${id}/comments?page=${page}&paginate=${paginate}`)

export const addInspirationComment = (id: number, body: { name: string; email: string; website?: string; comment: string }) =>
  postForm<{ comment: Comment }>(`inspirations/${id}/comments`, body)

export const toggleInspirationLike = (id: number) =>
  postForm<{ likes_count: number; liked: boolean }>(`inspirations/${id}/like`, {})

// ─── 29 · Insights ───────────────────────────────────────────────────────

export const getInsights = (page = 1, paginate = 15) =>
  get<{ insights: Insight[]; pagination: Pagination }>(`insights?page=${page}&paginate=${paginate}`)

// ─── 30 · Popular tags ───────────────────────────────────────────────────

export const getPopularTags = () => get<Tag[]>('popular-tags')

// ─── 31 · Contact (POST contact, form-data: full_name,email,subject,message)

export const sendContact = (body: { full_name: string; email: string; subject?: string; message: string }) =>
  postForm<null>('contact', body)

// ─── 32 · Subscribe (POST subscribe, form-data: email) ───────────────────

export const subscribe = (email: string) => postForm<null>('subscribe', { email })

// ─── 33-34 · Settings & Socials ──────────────────────────────────────────

export const getSettings = (keys: string[]) =>
  get<{ settings: Setting[] }>(`settings?${keys.map((k) => `keys[]=${k}`).join('&')}`)

export const getSiteSettings = () => getSettings(['site_logo', 'site_fav_icon', 'site_name', 'site_description'])

export const getSocials = () => get<Social[]>('socials')
